import { useState, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  LogOut, Menu, X, User, ChevronDown, Shield,
  PanelLeftClose, PanelLeft,
} from "lucide-react";
import { MENU_STRUCTURE, getPathToPermission } from "../config/menuConfig";
import NotificationsDropdown from "./NotificationsDropdown";
import SecurityAlertsDropdown from "./SecurityAlertsDropdown";
import { useSecurityAlerts } from "../hooks/useSecurityAlerts";
import { useTranslation } from "../contexts/I18nContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { SidebarNavigation } from "./layout/SidebarNavigation";

export default function TenantLayout({ children }) {
  const { tenant } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    contabilita: false, reportistica: false, anagrafiche: false,
    automezzi: false, appartamenti: false, budget: false,
    asset: false, produzione: false,
  });

  const token = localStorage.getItem('token');
  const { connected: alertsConnected, alerts: securityAlerts, unreadCount: alertsUnread, markAsRead, markAllAsRead, clearAlerts } = useSecurityAlerts(token);
  const { t, changeLang } = useTranslation();
  const tLabel = (item) => item.tKey ? t(item.tKey, item.label) : item.label;
  const pathToPermission = getPathToPermission();

  const hasPermission = (path, type = 'view') => {
    if (!user) return false;
    if (user.ruolo === 'admin') return true;
    const permKey = pathToPermission[path];
    if (!permKey) return true;
    const perm = (user.permissions || {})[permKey];
    return typeof perm === 'object' ? (type === 'edit' ? perm.edit : perm.view) : !!perm;
  };
  const canView = (path) => hasPermission(path, 'view');
  const canEdit = (path) => hasPermission(path, 'edit');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    const impersonatedTenant = localStorage.getItem('impersonatedTenant');
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedTenant) setTenantInfo(JSON.parse(storedTenant));
    // Mostra banner impersonazione SOLO se c'è anche il token superadmin di backup
    if (impersonatedTenant && localStorage.getItem('superAdminToken')) {
      setIsImpersonating(true);
    } else {
      // Pulisci stato orfano
      localStorage.removeItem('impersonatedTenant');
      setIsImpersonating(false);
    }
    if (savedCollapsed) setSidebarCollapsed(savedCollapsed === 'true');

    const syncLingua = async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/tenant/${tenant}/info`);
        if (resp.ok) {
          const info = await resp.json();
          if (info.lingua) {
            changeLang(info.lingua);
            const current = JSON.parse(localStorage.getItem('tenant') || '{}');
            if (current.lingua !== info.lingua) { current.lingua = info.lingua; localStorage.setItem('tenant', JSON.stringify(current)); }
          }
        }
      } catch { /* ignore network errors */ }
    };
    if (tenant) syncLingua();
  }, [tenant, changeLang]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280) setSidebarOpen(false);
      const saved = localStorage.getItem('sidebarCollapsed');
      if (window.innerWidth < 1536 && !saved) setSidebarCollapsed(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebarCollapsed = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };
  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const logout = () => {
    ['token', 'user', 'tenant', 'currentTenant', 'impersonatedTenant'].forEach(k => localStorage.removeItem(k));
    navigate(`/${tenant}/login`);
  };
  const exitImpersonation = () => {
    ['token', 'user', 'tenant', 'currentTenant', 'impersonatedTenant'].forEach(k => localStorage.removeItem(k));
    navigate(localStorage.getItem('superAdminToken') ? '/admin/dashboard' : '/admin');
  };

  const isActive = (path) => {
    const fullPath = `/${tenant}/${path}`;
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
  };

  const SidebarLogo = () => sidebarCollapsed ? (
    <div className="flex justify-center">
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25 ring-1 ring-white/10">
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white drop-shadow-sm" fill="none"><path d="M6 4v16M6 4l6 8-6 8M12 12l6-8v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/25 ring-1 ring-white/10">
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white drop-shadow-sm" fill="none"><path d="M6 4v16M6 4l6 8-6 8M12 12l6-8v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="font-bold text-xl tracking-tight bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">NOVA</h1>
        <p className="text-[9px] text-muted-foreground tracking-[0.2em] font-medium -mt-0.5">DATA • BUSINESS • MANAGEMENT</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="tenant-layout">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 bg-purple-600 text-white text-center py-2 z-[100] flex items-center justify-center gap-4">
          <Shield className="h-4 w-4" />
          <span className="text-sm font-medium">{t('common.viewingAs', 'Stai visualizzando come')}: {user?.nome} {user?.cognome} ({user?.email})</span>
          <Button size="sm" variant="secondary" onClick={exitImpersonation}>{t('common.exitSession', 'Esci dalla sessione')}</Button>
        </div>
      )}

      {/* Mobile header */}
      <header className={`lg:hidden fixed ${isImpersonating ? 'top-10' : 'top-0'} left-0 right-0 h-14 bg-card border-b z-50 flex items-center justify-between px-4`}>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="font-bold text-lg truncate">{tenantInfo?.nome || tenant}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><User className="h-5 w-5" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled className="text-xs">{user?.email}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}><LogOut className="h-4 w-4 mr-2" /> {t('common.logout', 'Esci')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed ${isImpersonating ? 'top-10' : 'top-0'} left-0 bottom-0 bg-card border-r z-50 transform transition-all duration-300 lg:translate-x-0 ${sidebarOpen ? "translate-x-0 w-[85vw] max-w-72 sm:w-64" : "-translate-x-full w-0"} ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} flex flex-col`}>
        <div className="p-3 border-b flex-shrink-0"><SidebarLogo /></div>
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          <SidebarNavigation menuStructure={MENU_STRUCTURE} tenant={tenant} expandedGroups={expandedGroups} toggleGroup={toggleGroup} isActive={isActive} canView={canView} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={() => setSidebarOpen(false)} />
        </nav>
        <div className="border-t p-2 flex-shrink-0">
          <div className="hidden lg:block mb-2">
            <Button variant="ghost" size="sm" onClick={toggleSidebarCollapsed} className={`w-full ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`} title={sidebarCollapsed ? "Espandi menu" : "Comprimi menu"}>
              {sidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4 mr-2" /><span className="text-xs">{t('common.collapse', 'Comprimi')}</span></>}
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 w-full p-2 rounded-md hover:bg-muted transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-white" /></div>
                {!sidebarCollapsed && <div className="flex-1 min-w-0 text-left"><p className="text-sm font-medium truncate">{user?.nome} {user?.cognome}</p><p className="text-xs text-muted-foreground truncate">{user?.ruolo}</p></div>}
                {!sidebarCollapsed && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={sidebarCollapsed ? "center" : "end"} side="top" className="w-56">
              <div className="px-2 py-1.5"><p className="text-sm font-medium">{user?.nome} {user?.cognome}</p><p className="text-xs text-muted-foreground">{user?.email}</p></div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600"><LogOut className="h-4 w-4 mr-2" /> {t('common.logout', 'Esci')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className={`transition-all duration-300 ${isImpersonating ? 'pt-10' : ''} pt-14 lg:pt-0 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} min-h-screen`}>
        <div className="hidden lg:flex items-center justify-between h-14 px-4 xl:px-6 border-b bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="flex items-center gap-4 min-w-0"><h2 className="text-base xl:text-lg font-semibold text-foreground/80 truncate">{tenantInfo?.nome || tenant}</h2></div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {user?.ruolo === 'admin' && <SecurityAlertsDropdown alerts={securityAlerts} unreadCount={alertsUnread} connected={alertsConnected} onMarkAsRead={markAsRead} onMarkAllAsRead={markAllAsRead} onClear={clearAlerts} />}
            <NotificationsDropdown />
          </div>
        </div>
        <div className="p-3 sm:p-4 lg:p-6 max-w-[2000px] mx-auto">{children}</div>
      </main>

      <footer className={`fixed bottom-0 right-0 p-2 text-xs text-muted-foreground transition-all duration-300 ${sidebarCollapsed ? 'lg:left-16' : 'lg:left-64'}`}>
        <span className="opacity-50">© 2025 NOVA-DBMS • Nova Data Business Management System</span>
      </footer>
    </div>
  );
}
